/**
 * Keep the phone screen awake while the player is racing. Browsers auto-
 * release the sentinel whenever the tab hides; we re-acquire on the next
 * visibilitychange. Failures (denied / unsupported / no API) are swallowed
 * so the caller can fire-and-forget.
 */

interface WakeLockSentinel {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (kind: string, cb: () => void) => void;
}

interface NavWithWakeLock {
  wakeLock?: { request: (kind: 'screen') => Promise<WakeLockSentinel> };
}

export class ScreenWakeLock {
  private sentinel: WakeLockSentinel | null = null;
  private wantHeld = false;
  private listening = false;
  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      // The browser auto-releases the sentinel; drop our reference so the
      // next 'visible' transition can re-acquire. We don't depend on the
      // sentinel's own 'release' event firing — some WebViews skip it.
      this.sentinel = null;
      return;
    }
    if (this.wantHeld && !this.sentinel) {
      void this.requestSentinel();
    }
  };

  attach(): void {
    if (this.listening) return;
    document.addEventListener('visibilitychange', this.onVisibility);
    this.listening = true;
  }

  detach(): void {
    if (!this.listening) return;
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.listening = false;
  }

  async acquire(): Promise<void> {
    this.wantHeld = true;
    await this.requestSentinel();
  }

  async release(): Promise<void> {
    this.wantHeld = false;
    if (!this.sentinel) return;
    try {
      await this.sentinel.release();
    } catch {
      // ignore
    }
    this.sentinel = null;
  }

  isHeld(): boolean {
    return this.sentinel !== null;
  }

  private async requestSentinel(): Promise<void> {
    const nav = (navigator as NavWithWakeLock).wakeLock;
    if (!nav) return;
    try {
      const sentinel = await nav.request('screen');
      this.sentinel = sentinel;
      sentinel.addEventListener('release', () => {
        // Browser-driven release (tab hide). Drop our reference so the next
        // visibilitychange can re-acquire.
        this.sentinel = null;
      });
    } catch {
      this.sentinel = null;
    }
  }
}
